import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import type { Workspace } from '../../../context/WorkspaceContext'
import { useToast } from '../../../context/useToast'
import { useNls } from '../../../nls/nlsProvider'
import { exportModuleChartPng, exportModuleCsv } from '../../../services/analysisExportService'
import type { AnalysisViewMode } from '../../../types/analysis'
import { Button } from '../../Common/Button'
import { MobileFullScreenPanel } from '../../Common/MobileFullScreenPanel'
import type { ChartDescriptor, ChartRecord } from '../charts'
import { ChartCanvasPanel } from '../popover/panels/ChartCanvasPanel'
import { RecordListPanel } from '../../Common'

export const MemberChartPanel = ({
  descriptor,
  onClose,
  viewMode = 'LIVE',
  workspace,
  snapshotId,
  isReducedViewer = false,
}: {
  descriptor: ChartDescriptor | null
  onClose: () => void
  viewMode?: AnalysisViewMode
  workspace?: Workspace
  snapshotId?: string
  isReducedViewer?: boolean
}) => {
  const { t } = useNls()
  const { showToast } = useToast()
  const canvasRef = useRef<HTMLDivElement | null>(null)
  const [records, setRecords] = useState<ChartRecord[]>([])
  const [loadingRecords, setLoadingRecords] = useState(false)

  useEffect(() => {
    if (!descriptor) return undefined
    let cancelled = false
    void Promise.resolve().then(() => {
      if (!cancelled) setLoadingRecords(true)
      return descriptor.buildRecords()
    }).then(
      (nextRecords) => {
        if (cancelled) return
        setRecords(nextRecords)
        setLoadingRecords(false)
      },
      () => {
        if (cancelled) return
        setRecords([])
        setLoadingRecords(false)
      },
    )
    return () => {
      cancelled = true
    }
  }, [descriptor])

  if (!descriptor) return null
  const exportDisabled = isReducedViewer || !workspace
  const csvDisabled = exportDisabled || viewMode !== 'LIVE' || loadingRecords || records.length === 0
  const viewerGateTitle = isReducedViewer ? t('NLSAnalysisViewerGateTooltip') : undefined

  const panel = (
    <MobileFullScreenPanel
      title={descriptor.title}
      eyebrow={`${descriptor.source.tab} · ${descriptor.source.cardLabel}`}
      onClose={onClose}
      uiName="UI-MPBRD-ANL-CHART"
      className="memberChartPanelOverlay"
      headerClassName="memberChartPanelOverlay__header"
      titleClassName="memberChartPanelOverlay__title"
      contentClassName="memberChartPanelOverlay__content"
      closeLabel={t('NLSAnalysisChartActionClose')}
    >
      <div className="memberChartPanel">
        <div className="memberChartPanel__canvas" ref={canvasRef}>
          <ChartCanvasPanel descriptor={descriptor} />
        </div>
        <div className="memberChartPanel__records">
          <RecordListPanel
            records={records}
            loading={loadingRecords}
            onDrillRecord={(record) => {
              onClose()
              descriptor.onDrillRecord?.(record)
            }}
          />
        </div>
        <footer className="memberChartPanel__exports">
          <Button
            variant="secondary"
            size="sm"
            icon={<ImageOutlinedIcon />}
            disabled={exportDisabled}
            title={viewerGateTitle}
            onClick={() => {
              const svg = canvasRef.current?.querySelector('svg') ?? null
              if (!svg || !workspace) {
                showToast(t('NLSAnalysisExportPngUnavailable'), 'warning')
                return
              }
              void exportModuleChartPng({ workspace, moduleId: descriptor.id, moduleTitle: descriptor.title, svgElement: svg, viewMode, snapshotId }).catch(() =>
                showToast(t('NLSAnalysisExportPngFailed'), 'error'),
              )
            }}
          >
            {t('NLSAnalysisExportModulePng')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            icon={<DownloadRoundedIcon />}
            disabled={csvDisabled}
            title={viewerGateTitle}
            onClick={() => {
              if (!workspace) return
              void exportModuleCsv({ workspace, moduleId: descriptor.id, moduleTitle: descriptor.title, records, snapshotId, viewMode }).catch(() => showToast(t('NLSAnalysisExportFailed'), 'error'))
            }}
          >
            {t('NLSAnalysisExportModuleCsv')}
          </Button>
        </footer>
      </div>
    </MobileFullScreenPanel>
  )

  return createPortal(panel, document.body)
}
